/**
 * 通用确认对话框组件。
 *
 * 删除、禁用等危险操作前的二次确认，支持危险态与处理中禁用。
 * 兼容 confirmColor 与 danger 两种危险态表达方式。
 *
 * @module components/common/ConfirmDialog
 */
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import type { ReactNode } from 'react';

/** 确认按钮颜色。 */
type ConfirmColor = 'primary' | 'error' | 'warning' | 'success';

/** ConfirmDialog 组件属性。 */
interface ConfirmDialogProps {
  /** 是否打开。 */
  open: boolean;
  /** 标题（默认“确认操作”）。 */
  title?: string;
  /** 提示内容。 */
  message: ReactNode;
  /** 确认按钮文案。 */
  confirmText?: string;
  /** 取消按钮文案。 */
  cancelText?: string;
  /** 是否危险操作（确认按钮变红），与 confirmColor 二选一。 */
  danger?: boolean;
  /** 确认按钮颜色（优先于 danger）。 */
  confirmColor?: ConfirmColor;
  /** 处理中（禁用按钮并显示文案）。 */
  loading?: boolean;
  /** 确认回调。 */
  onConfirm: () => void;
  /** 取消回调。 */
  onCancel: () => void;
}

/**
 * 通用确认对话框组件。
 *
 * @param props 组件属性。
 * @returns 对话框元素。
 */
export default function ConfirmDialog({
  open,
  title = '确认操作',
  message,
  confirmText = '确认',
  cancelText = '取消',
  danger = false,
  confirmColor,
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): JSX.Element {
  const color: ConfirmColor = confirmColor ?? (danger ? 'error' : 'primary');

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">{message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          color={color}
          variant="contained"
          disabled={loading}
        >
          {loading ? '处理中...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
